import { useCallback, useEffect, useState } from 'react'
import type { ConsentRegionMode } from '../../shared/privacyConsent'
import {
  getAnalyticsConsent,
  getMarketingConsent,
  getStoredAnalyticsConsent,
  getStoredMarketingConsent,
  setAnalyticsConsent,
  setMarketingConsent,
  startAnalytics,
  stopAnalytics,
  subscribeEffectiveAnalyticsConsent,
  subscribeEffectiveMarketingConsent,
  type AnalyticsConsent,
  type MarketingConsent,
} from './analytics'
import {
  getConsentRegionMode,
  resolveConsentRegionMode,
  subscribeConsentRegionMode,
} from './consentRegion'
import { startMetaPixel, stopMetaPixel } from './metaPixel'
import { captureMetaAttributionFromCurrentLocation, clearMetaAttribution } from './metaAttribution'

const NZ_NOTICE_STORAGE_KEY = 'sg.nz-tracking-notice.v1'
const NOTICE_MAX_AGE_MS = 180 * 24 * 60 * 60 * 1000

function noticeDismissed(): boolean {
  if (typeof window === 'undefined') return false
  try {
    const parsed = JSON.parse(window.localStorage.getItem(NZ_NOTICE_STORAGE_KEY) ?? '') as {
      version?: number
      expiresAt?: number
    }
    if (parsed.version !== 1 || typeof parsed.expiresAt !== 'number') return false
    if (Date.now() >= parsed.expiresAt) {
      window.localStorage.removeItem(NZ_NOTICE_STORAGE_KEY)
      return false
    }
    return true
  } catch {
    return false
  }
}

function dismissNotice(): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(NZ_NOTICE_STORAGE_KEY, JSON.stringify({
      version: 1,
      expiresAt: Date.now() + NOTICE_MAX_AGE_MS,
    }))
  } catch {
    // Dismissal persistence is optional; privacy controls still work.
  }
}

function usePrivacyConsent() {
  const [analyticsConsent, setAnalyticsState] = useState<AnalyticsConsent>(() => getAnalyticsConsent())
  const [marketingConsent, setMarketingState] = useState<MarketingConsent>(() => getMarketingConsent())
  const [regionMode, setRegionMode] = useState<ConsentRegionMode>(() => getConsentRegionMode())

  useEffect(() => subscribeEffectiveAnalyticsConsent(setAnalyticsState), [])
  useEffect(() => subscribeEffectiveMarketingConsent(setMarketingState), [])

  useEffect(() => {
    const unsubscribe = subscribeConsentRegionMode(setRegionMode)
    void resolveConsentRegionMode().then(setRegionMode)
    return unsubscribe
  }, [])

  useEffect(() => {
    // Read the current authority: another consent hook can resolve the region
    // between this render and its effects (including StrictMode replay).
    const currentConsent = getAnalyticsConsent()
    if (currentConsent === 'granted') {
      startAnalytics()
      return
    }
    // Region lookup is pending, not an opt-out. Keep identity cookies inert
    // until resolved; explicit denial still removes them immediately.
    stopAnalytics({ preserveCookies: currentConsent === 'unset' && getConsentRegionMode() === 'unknown' })
  }, [analyticsConsent, regionMode])

  useEffect(() => {
    const currentConsent = getMarketingConsent()
    if (currentConsent === 'granted') {
      captureMetaAttributionFromCurrentLocation()
      startMetaPixel()
      return
    }
    if (getStoredMarketingConsent() === 'denied') clearMetaAttribution()
    stopMetaPixel({ preserveCookies: currentConsent === 'unset' && getConsentRegionMode() === 'unknown' })
  }, [marketingConsent, regionMode])

  const chooseAnalytics = useCallback((choice: Exclude<AnalyticsConsent, 'unset'>) => {
    setAnalyticsConsent(choice)
  }, [])

  const chooseMarketing = useCallback((choice: Exclude<MarketingConsent, 'unset'>) => {
    setMarketingConsent(choice)
  }, [])

  const allowAll = useCallback(() => {
    setAnalyticsConsent('granted')
    setMarketingConsent('granted')
  }, [])

  const analyticsOnly = useCallback(() => {
    setAnalyticsConsent('granted')
    setMarketingConsent('denied')
  }, [])

  const declineAll = useCallback(() => {
    setAnalyticsConsent('denied')
    setMarketingConsent('denied')
  }, [])

  return {
    analyticsConsent,
    marketingConsent,
    regionMode,
    chooseAnalytics,
    chooseMarketing,
    allowAll,
    analyticsOnly,
    declineAll,
  }
}

function ManagePreferencesLink() {
  return <a className="analytics-consent-action analytics-consent-action--decline" href="/privacy#privacy-preferences">MANAGE PREFERENCES</a>
}

export function AnalyticsConsentCard() {
  const {
    analyticsConsent,
    marketingConsent,
    regionMode,
    chooseAnalytics,
    chooseMarketing,
    allowAll,
    analyticsOnly,
    declineAll,
  } = usePrivacyConsent()
  const [nzNoticeDismissed, setNzNoticeDismissed] = useState(() => noticeDismissed())

  if (regionMode === 'unknown') return null

  if (regionMode === 'nz-opt-out') {
    const hasImplicitDefault = getStoredAnalyticsConsent() === 'unset'
      || getStoredMarketingConsent() === 'unset'

    if (!hasImplicitDefault || nzNoticeDismissed) return null

    const acknowledge = () => {
      dismissNotice()
      setNzNoticeDismissed(true)
    }

    return (
      <aside className="analytics-consent-card analytics-consent-card--notice" aria-labelledby="analytics-consent-title">
        <div className="analytics-consent-card__copy">
          <h2 id="analytics-consent-title">A QUICK PRIVACY NOTE</h2>
          <p>For New Zealand visits, optional Google Analytics and Meta ad measurement are enabled by default unless you turn either one off. You can change them any time.</p>
        </div>
        <div className="analytics-consent-card__actions">
          <button className="analytics-consent-action analytics-consent-action--allow" type="button" onClick={acknowledge}>
            GOT IT
          </button>
          <ManagePreferencesLink />
        </div>
      </aside>
    )
  }

  if (analyticsConsent !== 'unset' && marketingConsent !== 'unset') return null

  if (analyticsConsent !== 'unset' && marketingConsent === 'unset') {
    return (
      <aside className="analytics-consent-card" aria-labelledby="analytics-consent-title">
        <div className="analytics-consent-card__copy">
          <h2 id="analytics-consent-title">ADVERTISING MEASUREMENT, YOUR CALL</h2>
          <p>Your existing analytics choice stays as it is. We can also use optional Meta tracking to measure whether Facebook and Instagram ads lead to visits and orders. Meta tracking stays off unless you allow it.</p>
        </div>
        <div className="analytics-consent-card__actions">
          <button className="analytics-consent-action analytics-consent-action--allow" type="button" onClick={() => chooseMarketing('granted')}>
            ALLOW AD MEASUREMENT
          </button>
          <button className="analytics-consent-action analytics-consent-action--decline" type="button" onClick={() => chooseMarketing('denied')}>
            KEEP IT OFF
          </button>
          <ManagePreferencesLink />
        </div>
      </aside>
    )
  }

  if (analyticsConsent === 'unset' && marketingConsent !== 'unset') {
    return (
      <aside className="analytics-consent-card" aria-labelledby="analytics-consent-title">
        <div className="analytics-consent-card__copy">
          <h2 id="analytics-consent-title">ANALYTICS, YOUR CALL</h2>
          <p>Google Analytics helps us understand how people use Sugar Gremlin and improve the site. Analytics stays off unless you allow it. Your advertising-measurement choice stays as it is.</p>
        </div>
        <div className="analytics-consent-card__actions">
          <button className="analytics-consent-action analytics-consent-action--allow" type="button" onClick={() => chooseAnalytics('granted')}>
            ALLOW ANALYTICS
          </button>
          <button className="analytics-consent-action analytics-consent-action--decline" type="button" onClick={() => chooseAnalytics('denied')}>
            KEEP IT OFF
          </button>
          <ManagePreferencesLink />
        </div>
      </aside>
    )
  }

  return (
    <aside className="analytics-consent-card" aria-labelledby="analytics-consent-title">
      <div className="analytics-consent-card__copy">
        <h2 id="analytics-consent-title">PRIVACY, YOUR CALL</h2>
        <p>We use optional Google Analytics to understand how people use Sugar Gremlin, and optional Meta tracking to measure whether Facebook and Instagram ads lead to visits and orders. Both stay off unless you allow them.</p>
      </div>
      <div className="analytics-consent-card__actions">
        <button className="analytics-consent-action analytics-consent-action--allow" type="button" onClick={allowAll}>
          ALLOW ALL
        </button>
        <button className="analytics-consent-action analytics-consent-action--decline" type="button" onClick={analyticsOnly}>
          ANALYTICS ONLY
        </button>
        <button className="analytics-consent-action analytics-consent-action--decline" type="button" onClick={declineAll}>
          NO THANKS
        </button>
        <ManagePreferencesLink />
      </div>
    </aside>
  )
}

function consentStatus(
  effective: AnalyticsConsent | MarketingConsent,
  stored: AnalyticsConsent | MarketingConsent,
  regionMode: ConsentRegionMode,
) {
  if (effective === 'granted' && stored === 'unset' && regionMode === 'nz-opt-out') return 'On (NZ default)'
  if (effective === 'granted') return 'On'
  if (effective === 'denied') return 'Off'
  return 'Not chosen'
}

export function AnalyticsPreferences() {
  const {
    analyticsConsent,
    marketingConsent,
    regionMode,
    chooseAnalytics,
    chooseMarketing,
  } = usePrivacyConsent()
  const [confirmation, setConfirmation] = useState('')

  const analyticsOn = analyticsConsent === 'granted'
  const marketingOn = marketingConsent === 'granted'
  const storedAnalytics = getStoredAnalyticsConsent()
  const storedMarketing = getStoredMarketingConsent()

  const toggleAnalytics = () => {
    const next = analyticsOn ? 'denied' : 'granted'
    chooseAnalytics(next)
    setConfirmation(`Analytics is now ${next === 'granted' ? 'on' : 'off'}.`)
  }

  const toggleMarketing = () => {
    const next = marketingOn ? 'denied' : 'granted'
    chooseMarketing(next)
    setConfirmation(`Advertising measurement is now ${next === 'granted' ? 'on' : 'off'}.`)
  }

  return (
    <section className="analytics-preferences" id="privacy-preferences" aria-labelledby="privacy-preferences-title">
      <h2 id="privacy-preferences-title">MANAGE PREFERENCES</h2>
      <p>
        These controls are independent. For New Zealand visits they are on by default unless you turn them off.
        For visits not confirmed as New Zealand, they stay off until allowed. You can use Sugar Gremlin and place
        an order with either or both turned off.
      </p>

      <div className="analytics-preferences__choice">
        <h3>Analytics</h3>
        <p>Google Analytics 4 helps us understand how people use the website and improve the experience.</p>
        <p className="analytics-preferences__status"><strong>Analytics status:</strong> {consentStatus(analyticsConsent, storedAnalytics, regionMode)}</p>
        <button className="analytics-preferences__action" type="button" onClick={toggleAnalytics}>
          {analyticsOn ? 'TURN OFF ANALYTICS' : 'ALLOW ANALYTICS'}
        </button>
      </div>

      <div className="analytics-preferences__choice">
        <h3>Advertising Measurement</h3>
        <p>Meta advertising measurement, including Meta Pixel and the server-side Conversions API, helps us measure whether Facebook and Instagram advertising leads to website visits and paid orders. We do not deliberately send your checkout name, email, phone, address, postcode or delivery notes through these advertising-measurement events.</p>
        <p className="analytics-preferences__status"><strong>Advertising measurement status:</strong> {consentStatus(marketingConsent, storedMarketing, regionMode)}</p>
        <button className="analytics-preferences__action" type="button" onClick={toggleMarketing}>
          {marketingOn ? 'TURN OFF AD MEASUREMENT' : 'ALLOW AD MEASUREMENT'}
        </button>
      </div>

      <p className="analytics-preferences__confirmation" role="status" aria-live="polite" aria-atomic="true">
        {confirmation}
      </p>
    </section>
  )
}
